/**
 * The pane's chat session for one open document (spec §2.1, §5.2, §8):
 *
 *   send:  capture -> snapshots/init -> gzip upload -> POST chat/jobs {message, word:{snapshot_id}, thread_id?}
 *          -> poll (poll_after_ms) -> answer + word_artifacts
 *   cards: apply / add comment / insert / dismiss, each through the hands' global queue, with a stale guard
 *          (live doc hash vs the artifact's doc_hash -> per-card re-anchor and honest statuses).
 *
 * Everything lives in memory for the pane session: the thread id per doc_key (named by the server on the
 * job), the ids of comments this pane created ("Remove Ella's comments" touches only those), card states.
 * Nothing but `doc_key` is written into the document. When the document has saved versions (spec §3.3) the
 * comment ids are also kept on the newest version server-side, so a reloaded pane can still remove them.
 *
 * Each turn also reports what the user did with the previous turn's cards (`word.last_apply_report`), so
 * Ella knows what was applied, dismissed or refused.
 */

import { PID_PATTERN, type WordSnapshot } from "../anchor/snapshot";
import {
  type ApplyOutcome,
  type ApplyReportItem,
  AuthLostError,
  type ChatBackend,
  type JobCitation,
  type JobPoll,
  type SavedVersion,
} from "../backends/types";
import type {
  ApplyResult,
  DocumentHands,
  InsertRequest,
  ModeJournal,
  PaneComment,
  SelectionTarget,
  TrackingMode,
} from "../hands/types";
import { activityLine } from "./activity";
import { type EditArtifact, type WordArtifacts, editsInDocumentOrder, pidLabel } from "./artifacts";
import { type DocKey, type DocSettings, peekDocKey, resolveDocKey } from "./docKey";
import { type TurnStep, turnErrorMessage } from "./errors";
import { FLIP_MAX_AGE_MS, type SessionStore, clearFlip, defaultSessionStore, modeLabel, readFlip, writeFlip } from "./trackingJournal";

export type TurnPhase = "reading" | "uploading" | "starting" | "working" | "done" | "failed";

export interface TurnDebug {
  paragraphs: number;
  chars: number;
  captureMs: number;
  rawBytes?: number;
  gzipBytes?: number;
  uploadMs?: number;
  requestId?: string;
  docKeyPersisted?: boolean;
}

export interface Turn {
  id: string;
  message: string;
  phase: TurnPhase;
  activity: string;
  /** The pre-final draft while Ella writes (shown muted). */
  draft: string | null;
  answer: string | null;
  citations: JobCitation[];
  artifacts: WordArtifacts | null;
  /** The snapshot Ella read this turn (held in memory: paragraph hashes and change authors for the cards). */
  snapshot: WordSnapshot | null;
  /** The selection/cursor when Ella read the document: what "Replace selection" / "Insert at cursor" act on. */
  selectionTarget: SelectionTarget | null;
  requestId: string | null;
  error: string | null;
  /** The error was a lost session: the pane shows Reconnect instead of Try again. */
  authLost: boolean;
  degraded: boolean;
  /** An apply found the document changed since Ella read it. */
  docChanged: boolean;
  debug: TurnDebug | null;
}

export type CardStatus =
  | { state: "idle" }
  | { state: "working" }
  /** `warning`: done, but something the user should check (reported to the model as the reason). */
  | { state: "done"; text: string; warning?: string }
  | { state: "failed"; reason: "changed" | "place" | "refused" | "error"; text: string }
  | { state: "dismissed" };

export interface CardState {
  status: CardStatus;
  /** Edit cards: put the why in the Word comment (default off, spec D4). */
  includeReason: boolean;
  /** Edit cards: the edit is applied but its Word comment is not; where to add it on "Retry comment". */
  retryComment?: PaneComment | null;
  /** A comment retry is running. */
  commentBusy?: boolean;
}

export interface ApplyAllState {
  running: boolean;
  summary: string | null;
}

export interface ChatState {
  turns: readonly Turn[];
  busy: boolean;
  cards: Readonly<Record<string, CardState>>;
  applyAll: Readonly<Record<string, ApplyAllState>>;
  /** Word comment ids this pane created this session. */
  ellaCommentIds: readonly string[];
  trackingMode: TrackingMode | null;
  /** Ella switched Track Changes on and it was never put back (pane closed/crashed, or the restore failed). */
  trackingLeftOff: { previousMode: TrackingMode; currentMode: TrackingMode } | null;
  notice: { tone: "info" | "error"; text: string } | null;
  /** The conversation this document's turns continue (null until the first turn names one). */
  threadId: string | null;
  /** Saved versions of this document, newest first, in every conversation (each names its thread). */
  versions: readonly SavedVersion[];
  versionsLoaded: boolean;
  /** A version save or delete is in flight. */
  versionBusy: boolean;
}

export interface ChatDeps {
  hands: DocumentHands | null;
  backend: ChatBackend;
  /** The live bearer token, or null once the session ended. */
  token: () => string | null;
  /** Called when the backend rejected the token (401): the app shows Reconnect. */
  onAuthLost: () => void;
  /** Explicit user actions only (send, apply, add, insert): keeps the web session alive (spec §6.1). */
  onUserAction: () => void;
  docSettings: DocSettings | null;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
  newId?: () => string;
  mintDocKey?: () => string;
  /** Where the Track Changes "left off" note lives; defaults to sessionStorage (null: nowhere). */
  sessionStore?: SessionStore | null;
}

const DEFAULT_CARD: CardState = { status: { state: "idle" }, includeReason: false };
/** Comment ids sent per request (the server takes at most 100 at a time). */
const ELLA_ID_CHUNK = 100;
const RESTORE_WARNING = "Track Changes could not be put back as it was. Check it on Word's Review tab.";

/** Why a comment about a footnote sits on the footnote's number in the text (Word on the web, spike P2 B3). */
export function noteCommentText(label: string): string {
  return ` The comment is on the ${label} number in the text: Word on the web can't keep comments inside footnotes.`;
}
export const PROTECTED_NOTICE =
  "Word won't let Ella change this document: it is protected or open read-only. Nothing was changed. Turn off the protection (Review › Restrict Editing) or save an editable copy, then apply again.";

interface Freshness {
  same: boolean;
  /** Snapshot paragraphs whose text is nowhere in the live document any more. */
  changed: ReadonlySet<string>;
  /** Paragraphs were added or removed by someone other than this pane. */
  countChanged: boolean;
}

/**
 * Where each paragraph of a turn's snapshot is now, as far as this pane's own edits moved it. An edit that adds
 * a paragraph shifts every later index in its story, so a card is re-anchored by the hash of its paragraph
 * (the snapshot's, or the one Word reported right after this pane edited it), with the index only as a hint.
 */
interface TurnLayout {
  /** Snapshot pid -> its current pid and hash, for paragraphs this pane edited. */
  edited: Map<string, { pid: string; hash?: string }>;
  /** Paragraph count changes this pane made, in order: indices after `after` in `story` moved by `delta`. */
  shifts: { story: string; after: number; delta: number }[];
  /** Net paragraphs this pane added (so a count change of exactly this much is not someone else's). */
  ownDelta: number;
}

function splitPid(pid: string): { story: string; index: number } | null {
  const m = /^(p|fn\d+\.|en\d+\.|hf\d+\.)(\d+)$/.exec(pid);
  return m ? { story: m[1] as string, index: Number(m[2]) } : null;
}

function shiftedPid(pid: string, shifts: TurnLayout["shifts"]): string {
  const ref = splitPid(pid);
  if (ref === null) return pid;
  let index = ref.index;
  for (const s of shifts) if (s.story === ref.story && index > s.after) index += s.delta;
  return `${ref.story}${Math.max(0, index)}`;
}

export function cardKey(turnId: string, itemId: string): string {
  return `${turnId}:${itemId}`;
}

/** The chat line that asks Ella to compare the open document with a saved version (it names the id). */
export function comparePrompt(version: Pick<SavedVersion, "name" | "versionId">): string {
  return `Compare this document with my saved version “${version.name}” (version_id ${version.versionId}) and tell me what changed.`;
}

function reportKey(itemId: string): Pick<ApplyReportItem, "edit_id" | "comment_id" | "insert_id"> {
  if (itemId.startsWith("e")) return { edit_id: itemId };
  if (itemId.startsWith("c")) return { comment_id: itemId };
  return { insert_id: itemId };
}

let turnCounter = 0;

/** A container id the agent loop accepts (`thread-` prefix); the random tail keeps two documents apart. */
export function mintThreadId(nowMs: number): string {
  return `thread-${nowMs}-${Math.random().toString(36).slice(2, 10)}`;
}

export class ChatController {
  private state: ChatState = {
    turns: [],
    busy: false,
    cards: {},
    applyAll: {},
    ellaCommentIds: [],
    trackingMode: null,
    trackingLeftOff: null,
    notice: null,
    threadId: null,
    versions: [],
    versionsLoaded: false,
    versionBusy: false,
  };
  private readonly listeners = new Set<() => void>();
  private readonly threads = new Map<string, string>();
  /** Per turn: what the user did with each card (sent with the next turn as `last_apply_report`). */
  private readonly reports = new Map<string, Map<string, ApplyReportItem>>();
  /** Turns whose report already went up with a later turn. */
  private readonly reported = new Set<string>();
  private docKeyPromise: Promise<DocKey> | null = null;
  /** Paragraphs this pane changed since each turn's snapshot (their changes are not "the document changed"). */
  private readonly touched = new Map<string, Set<string>>();
  private readonly layouts = new Map<string, TurnLayout>();
  /** Word comment id -> the card that created it (and whether that card was a comment card). */
  private readonly commentCards = new Map<string, { key: string; commentCard: boolean }>();
  private generation = 0;

  constructor(private readonly deps: ChatDeps) {}

  // ---- store -------------------------------------------------------------------------------------------------

  getState = (): ChatState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private patch(update: Partial<ChatState>): void {
    this.state = { ...this.state, ...update };
    for (const listener of this.listeners) listener();
  }

  private patchTurn(id: string, update: Partial<Turn>): void {
    this.patch({ turns: this.state.turns.map((t) => (t.id === id ? { ...t, ...update } : t)) });
  }

  private turn(id: string): Turn | undefined {
    return this.state.turns.find((t) => t.id === id);
  }

  card(turnId: string, itemId: string): CardState {
    return this.state.cards[cardKey(turnId, itemId)] ?? DEFAULT_CARD;
  }

  private setCard(turnId: string, itemId: string, update: Partial<CardState>): void {
    const key = cardKey(turnId, itemId);
    this.patch({ cards: { ...this.state.cards, [key]: { ...(this.state.cards[key] ?? DEFAULT_CARD), ...update } } });
  }

  dismissNotice(): void {
    this.patch({ notice: null });
  }

  /** Forget everything (a different account connected in this pane). */
  reset(): void {
    this.generation++;
    this.threads.clear();
    this.touched.clear();
    this.layouts.clear();
    this.commentCards.clear();
    this.reports.clear();
    this.reported.clear();
    this.patch({
      turns: [],
      busy: false,
      cards: {},
      applyAll: {},
      ellaCommentIds: [],
      notice: null,
      threadId: null,
      versions: [],
      versionsLoaded: false,
      versionBusy: false,
    });
  }

  /** Start a new conversation about this document (the old thread stays in the user's history). */
  async newChat(): Promise<void> {
    if (this.state.busy) return;
    const key = await this.docKey();
    this.threads.delete(key.key);
    this.reports.clear();
    this.reported.clear();
    this.patch({ turns: [], cards: {}, applyAll: {}, notice: null, threadId: null });
  }

  private setThread(key: string, threadId: string): void {
    this.threads.set(key, threadId);
    if (this.state.threadId !== threadId) this.patch({ threadId });
  }

  /** The thread this document's turns continue (for tests and the debug footer). */
  async threadId(): Promise<string | null> {
    return this.threads.get((await this.docKey()).key) ?? null;
  }

  private docKey(): Promise<DocKey> {
    this.docKeyPromise ??= resolveDocKey(this.deps.docSettings, this.deps.mintDocKey);
    return this.docKeyPromise;
  }

  async refreshTrackingMode(): Promise<void> {
    if (this.deps.hands === null) return;
    try {
      this.patch({ trackingMode: await this.deps.hands.getTrackingMode() });
    } catch {
      this.patch({ trackingMode: null });
    }
  }

  // ---- a turn ------------------------------------------------------------------------------------------------

  async send(rawMessage: string): Promise<void> {
    const message = rawMessage.trim();
    const hands = this.deps.hands;
    if (!message || this.state.busy) return;
    this.deps.onUserAction();
    const generation = this.generation;
    const id = this.deps.newId?.() ?? `t${++turnCounter}`;
    const turn: Turn = {
      id,
      message,
      phase: "reading",
      activity: "Reading the document…",
      draft: null,
      answer: null,
      citations: [],
      artifacts: null,
      snapshot: null,
      selectionTarget: null,
      requestId: null,
      error: null,
      authLost: false,
      degraded: false,
      docChanged: false,
      debug: null,
    };
    this.patch({ turns: [...this.state.turns, turn], busy: true, notice: null });
    const live = (): boolean => generation === this.generation;
    let step: TurnStep = "read";
    try {
      if (hands === null) throw new Error("Open this pane inside Word to use Ella.");
      const token = this.deps.token();
      if (token === null) throw new AuthLostError();
      const docKey = await this.docKey();

      const captured = await hands.capture();
      if (!live()) return;
      const debug: TurnDebug = {
        paragraphs: captured.snapshot.paragraphs.length,
        chars: captured.snapshot.char_count,
        captureMs: captured.snapshot.capture_ms,
        docKeyPersisted: docKey.persisted,
      };
      this.patchTurn(id, { phase: "uploading", activity: "Sending the document to Ella securely…", debug, selectionTarget: captured.target });

      step = "upload";
      const ticket = await this.deps.backend.initSnapshot(token);
      const snapshot: WordSnapshot = { ...captured.snapshot, snapshot_id: ticket.snapshotId };
      const upload = await this.deps.backend.uploadSnapshot(ticket, snapshot);
      if (!live()) return;
      const withUpload = { ...debug, rawBytes: upload.rawBytes, gzipBytes: upload.gzipBytes, uploadMs: upload.ms };
      this.patchTurn(id, { phase: "starting", activity: "Starting…", snapshot, debug: withUpload });

      step = "start";
      // A new conversation's id is minted here, as the Ask SPA does: the agent loop resolves the job's thread
      // as a container, and a container id must start with "thread-". Letting the server fall back to the
      // request id ("req_…") failed every first turn with project_unavailable.
      const threadId = this.threads.get(docKey.key) ?? mintThreadId((this.deps.now ?? Date.now)());
      const report = this.pendingReport(id);
      const first = await this.deps.backend.createJob(token, {
        message,
        word: { snapshot_id: ticket.snapshotId, ...(report.items.length ? { last_apply_report: report.items } : {}) },
        thread_id: threadId,
      });
      if (!live()) return;
      if (report.turnId !== null) this.reported.add(report.turnId);
      this.setThread(docKey.key, first.threadId ?? threadId);
      this.patchTurn(id, { phase: "working", requestId: first.requestId, debug: { ...withUpload, requestId: first.requestId } });

      step = "wait";
      const final = await this.deps.backend.waitForJob(token, first, {
        sleep: this.deps.sleep,
        now: this.deps.now,
        onUpdate: (poll) => {
          if (live() && !poll.terminal) this.patchTurn(id, { activity: activityLine(poll), draft: poll.draftAnswer });
        },
      });
      if (!live()) return;
      if (final.threadId && final.threadId !== this.threads.get(docKey.key)) this.setThread(docKey.key, final.threadId);
      this.finish(id, final);
    } catch (error) {
      if (!live()) return;
      const authLost = error instanceof AuthLostError;
      this.patchTurn(id, { phase: "failed", error: turnErrorMessage(error, step), authLost, activity: "" });
      if (authLost) this.deps.onAuthLost();
    } finally {
      if (live()) this.patch({ busy: false });
    }
    if (live()) await this.refreshTrackingMode();
  }

  /** The newest earlier turn with cards whose report has not gone up yet, and its report. */
  private pendingReport(currentTurnId: string): { turnId: string | null; items: ApplyReportItem[] } {
    const previous = [...this.state.turns].reverse().find((t) => t.id !== currentTurnId && t.artifacts !== null);
    if (previous === undefined || this.reported.has(previous.id)) return { turnId: null, items: [] };
    return { turnId: previous.id, items: [...(this.reports.get(previous.id)?.values() ?? [])] };
  }

  private report(turnId: string, itemId: string, pid: string, outcome: ApplyOutcome | null, reason?: string): void {
    let byItem = this.reports.get(turnId);
    if (byItem === undefined) {
      byItem = new Map();
      this.reports.set(turnId, byItem);
    }
    if (outcome === null) {
      byItem.delete(itemId);
      return;
    }
    const item: ApplyReportItem = { ...reportKey(itemId), outcome };
    if (PID_PATTERN.test(pid)) item.pid = pid;
    if (reason) item.reason = reason;
    byItem.set(itemId, item);
  }

  /** Record a card's result for the next turn's report. */
  private reportStatus(turnId: string, itemId: string, pid: string, status: CardStatus, doneOutcome: ApplyOutcome): void {
    if (status.state === "done") this.report(turnId, itemId, pid, doneOutcome, status.warning);
    else if (status.state === "failed") this.report(turnId, itemId, pid, status.reason === "changed" ? "stale" : "failed", status.text);
  }

  private finish(id: string, poll: JobPoll): void {
    if (poll.status === "failed" || poll.status === "expired") {
      const error =
        poll.status === "expired"
          ? "This request expired before Ella finished. Send it again."
          : (poll.errorMessage ?? "Ella couldn't finish this request.").replace(/\bScholar\b/g, "Ella");
      this.patchTurn(id, { phase: "failed", error, activity: "", draft: null, requestId: poll.requestId });
      return;
    }
    this.patchTurn(id, {
      phase: "done",
      activity: "",
      draft: null,
      answer: poll.answer,
      citations: poll.citations,
      artifacts: poll.wordArtifacts,
      degraded: poll.status === "degraded",
    });
  }

  // ---- stale guard -------------------------------------------------------------------------------------------

  /** Compare the live document with the snapshot this turn's artifacts were verified against (§5.2). */
  private async freshness(turn: Turn): Promise<Freshness | null> {
    const hands = this.deps.hands;
    const base = turn.snapshot;
    if (hands === null || base === null || turn.artifacts === null) return null;
    let live: WordSnapshot;
    try {
      live = (await hands.capture()).snapshot;
    } catch {
      return null; // Can't tell; each card still re-checks its own paragraph by hash.
    }
    const expected = turn.artifacts.doc_hash || base.doc_hash;
    if (live.doc_hash === expected) return { same: true, changed: new Set(), countChanged: false };
    // By content, not by index: a paragraph that only moved (this pane added one above it) did not change.
    const liveHashes = new Set(live.paragraphs.map((p) => p.hash));
    const changed = new Set(base.paragraphs.filter((p) => !liveHashes.has(p.hash)).map((p) => p.pid));
    const ownDelta = this.layoutOf(turn.id).ownDelta;
    const fresh = { same: false, changed, countChanged: live.paragraphs.length !== base.paragraphs.length + ownDelta };
    if (!turn.docChanged) this.patchTurn(turn.id, { docChanged: true });
    return fresh;
  }

  private layoutOf(turnId: string): TurnLayout {
    let layout = this.layouts.get(turnId);
    if (layout === undefined) {
      layout = { edited: new Map(), shifts: [], ownDelta: 0 };
      this.layouts.set(turnId, layout);
    }
    return layout;
  }

  private touchedIn(turnId: string): Set<string> {
    let set = this.touched.get(turnId);
    if (set === undefined) {
      set = new Set();
      this.touched.set(turnId, set);
    }
    return set;
  }

  /**
   * Where the hands should look for a card's paragraph: a pid (only a hint) and the hash that must match. For a
   * paragraph this pane already edited that is the hash Word reported after the edit; otherwise the snapshot's.
   * The hash is never dropped, so a card never lands by index alone (red-team M6).
   */
  private anchorHint(turn: Turn, pid: string, artifactHash = ""): { pid: string; paragraphHash?: string } {
    const layout = this.layoutOf(turn.id);
    const own = layout.edited.get(pid);
    if (own !== undefined) return { pid: own.pid, ...(own.hash ? { paragraphHash: own.hash } : {}) };
    const hash = turn.snapshot?.paragraphs.find((p) => p.pid === pid)?.hash ?? (artifactHash || undefined);
    return { pid: shiftedPid(pid, layout.shifts), ...(hash ? { paragraphHash: hash } : {}) };
  }

  /** Record where an applied edit landed and how it moved the paragraphs after it. */
  private recordApplied(turnId: string, snapshotPid: string, result: ApplyResult): void {
    const layout = this.layoutOf(turnId);
    if (result.pid === undefined || result.paragraphHashAfter === undefined) {
      // Word could not be re-read: forget any hash we held, so the quote is found by its text.
      layout.edited.delete(snapshotPid);
      return;
    }
    const delta = result.paragraphDelta ?? 0;
    const at = splitPid(result.pid);
    if (delta !== 0 && at !== null) {
      for (const [key, entry] of layout.edited) {
        const ref = splitPid(entry.pid);
        if (ref !== null && ref.story === at.story && ref.index > at.index) layout.edited.set(key, { ...entry, pid: `${ref.story}${ref.index + delta}` });
      }
      layout.shifts.push({ story: at.story, after: at.index, delta });
      layout.ownDelta += delta;
    }
    layout.edited.set(snapshotPid, { pid: result.pid, hash: result.paragraphHashAfter });
  }

  private changedHere(turn: Turn, pid: string, fresh: Freshness | null): boolean {
    if (fresh === null || fresh.same) return false;
    return fresh.countChanged || (fresh.changed.has(pid) && !this.touchedIn(turn.id).has(pid));
  }

  // ---- cards -------------------------------------------------------------------------------------------------

  setIncludeReason(turnId: string, editId: string, value: boolean): void {
    this.setCard(turnId, editId, { includeReason: value });
  }

  dismiss(turnId: string, itemId: string): void {
    this.setCard(turnId, itemId, { status: { state: "dismissed" } });
    this.report(turnId, itemId, this.cardPid(turnId, itemId), "dismissed");
  }

  restore(turnId: string, itemId: string): void {
    this.setCard(turnId, itemId, { status: { state: "idle" } });
    this.report(turnId, itemId, "", null);
  }

  private cardPid(turnId: string, itemId: string): string {
    const a = this.turn(turnId)?.artifacts;
    return (
      a?.edits.find((e) => e.edit_id === itemId)?.pid ??
      a?.comments.find((c) => c.comment_id === itemId)?.pid ??
      a?.inserts.find((i) => i.insert_id === itemId)?.pid ??
      ""
    );
  }

  private rememberComment(id: string | undefined, turnId: string, itemId: string, commentCard: boolean): void {
    if (!id) return;
    this.commentCards.set(id, { key: cardKey(turnId, itemId), commentCard });
    this.patch({ ellaCommentIds: [...this.state.ellaCommentIds, id] });
    void this.syncEllaComments([id], []);
  }

  private applyStatus(result: ApplyResult, mode: "tracked" | "clean", changedHere: boolean): CardStatus {
    const restore = result.modeRestoreFailed ? ` ${RESTORE_WARNING}` : "";
    switch (result.status) {
      case "applied":
      case "applied_unverified": {
        let text = mode === "tracked" ? "Applied as a tracked change." : "Applied without tracking.";
        if (result.imprecise) text = `${text.slice(0, -1)}, but Ella couldn't insert it precisely: the whole quoted passage was replaced.`;
        if (result.commentError) text += " The comment couldn't be added.";
        else if (result.commentOnNote) text += noteCommentText(result.commentOnNote);
        if (result.status === "applied" && result.detail) text += ` ${result.detail}`;
        const done: CardStatus = { state: "done", text: text + restore };
        return result.status === "applied_unverified" ? { ...done, warning: result.detail } : done;
      }
      case "noop":
        return { state: "done", text: "The document already says this. Nothing changed." };
      case "refused":
        return { state: "failed", reason: "refused", text: result.detail + restore };
      case "not_found":
      case "ambiguous":
        return changedHere
          ? { state: "failed", reason: "changed", text: `Document changed here. ${result.detail}` }
          : { state: "failed", reason: "place", text: `Couldn't place: ${result.detail}` };
      case "pending_change":
      case "locked":
        return { state: "failed", reason: "place", text: `Couldn't place: ${result.detail}` };
      default:
        return { state: "failed", reason: "error", text: `Couldn't place: Word reported an error. ${result.detail}${restore}` };
    }
  }

  /** Word refused because the document is protected/read-only: one notice, and the card is left as it was. */
  private protectedDocument(): void {
    this.patch({ notice: { tone: "error", text: PROTECTED_NOTICE } });
  }

  /** The Track Changes journal for this document (see `trackingJournal.ts`). */
  private async journal(): Promise<ModeJournal> {
    const key = await this.docKey();
    const store = this.store();
    const now = this.deps.now ?? Date.now;
    return {
      begin: (previousMode) => writeFlip(store, { docKey: key.key, persisted: key.persisted, previousMode, startedAt: now() }),
      end: () => clearFlip(store),
    };
  }

  private store(): SessionStore | null {
    return this.deps.sessionStore === undefined ? defaultSessionStore() : this.deps.sessionStore;
  }

  /** `protected` when Word refused the whole document; the card is then back to idle. */
  private async applyOne(turn: Turn, edit: EditArtifact, mode: "tracked" | "clean", fresh: Freshness | null): Promise<CardStatus | "protected"> {
    const hands = this.deps.hands;
    if (hands === null) return { state: "failed", reason: "error", text: "Open this pane inside Word to apply edits." };
    const card = this.card(turn.id, edit.edit_id);
    this.setCard(turn.id, edit.edit_id, { status: { state: "working" } });
    let status: CardStatus;
    let retryComment: PaneComment | null = null;
    try {
      const hint = this.anchorHint(turn, edit.pid, edit.paragraph_hash);
      const result = await hands.applyEdit(
        { id: edit.edit_id, ...hint, quote: edit.quote, replacement: edit.replacement, op: edit.op, why: edit.why },
        { mode, includeReason: card.includeReason, journal: await this.journal() },
      );
      if (result.status === "protected") {
        this.setCard(turn.id, edit.edit_id, { status: { state: "idle" } });
        this.protectedDocument();
        return "protected";
      }
      if (result.status === "applied" || result.status === "applied_unverified") {
        this.touchedIn(turn.id).add(edit.pid);
        this.recordApplied(turn.id, edit.pid, result);
        this.rememberComment(result.commentId, turn.id, edit.edit_id, false);
        retryComment = result.retryComment ?? null;
      }
      if (result.modeRestoreFailed) {
        this.patch({ notice: { tone: "error", text: RESTORE_WARNING } });
        await this.checkLeftOffTracking();
      }
      status = this.applyStatus(result, mode, this.changedHere(turn, edit.pid, fresh));
    } catch (error) {
      status = { state: "failed", reason: "error", text: `Couldn't place: ${error instanceof Error ? error.message : String(error)}` };
    }
    this.setCard(turn.id, edit.edit_id, { status, retryComment });
    this.reportStatus(turn.id, edit.edit_id, edit.pid, status, mode === "clean" ? "clean" : "applied");
    return status;
  }

  private findEdit(turnId: string, editId: string): { turn: Turn; edit: EditArtifact } | null {
    const turn = this.turn(turnId);
    const edit = turn?.artifacts?.edits.find((e) => e.edit_id === editId);
    return turn && edit ? { turn, edit } : null;
  }

  async applyEdit(turnId: string, editId: string, mode: "tracked" | "clean"): Promise<CardStatus | null> {
    const found = this.findEdit(turnId, editId);
    if (found === null) return null;
    this.deps.onUserAction();
    this.setCard(turnId, editId, { status: { state: "working" } });
    const fresh = await this.freshness(found.turn);
    const outcome = await this.applyOne(this.turn(turnId) ?? found.turn, found.edit, mode, fresh);
    await this.refreshTrackingMode();
    return outcome === "protected" ? { state: "idle" } : outcome;
  }

  /**
   * Apply every pending edit of a turn as tracked changes, one after another, in document order. Each edit
   * re-anchors by its paragraph's hash as Word reported it after the edits before it (red-team M6), and a
   * protected document stops the run with one message instead of failing every card.
   */
  async applyAllTracked(turnId: string): Promise<CardStatus[]> {
    const turn = this.turn(turnId);
    if (turn === undefined || turn.artifacts === null || this.state.applyAll[turnId]?.running) return [];
    const pending = editsInDocumentOrder(turn.artifacts, turn.snapshot).filter((e) => {
      const s = this.card(turnId, e.edit_id).status.state;
      return s === "idle" || s === "failed";
    });
    if (pending.length === 0) return [];
    this.deps.onUserAction();
    this.patch({ applyAll: { ...this.state.applyAll, [turnId]: { running: true, summary: `Applying ${pending.length}…` } } });
    for (const e of pending) this.setCard(turnId, e.edit_id, { status: { state: "working" } });
    const fresh = await this.freshness(turn);
    const results: CardStatus[] = [];
    let stopped = false;
    for (const edit of pending) {
      if (stopped) {
        this.setCard(turnId, edit.edit_id, { status: { state: "idle" } });
        continue;
      }
      const outcome = await this.applyOne(this.turn(turnId) ?? turn, edit, "tracked", fresh);
      if (outcome === "protected") stopped = true;
      else results.push(outcome);
    }
    const applied = results.filter((r) => r.state === "done").length;
    const unverified = results.filter((r) => r.state === "done" && r.warning).length;
    const changed = results.filter((r) => r.state === "failed" && r.reason === "changed").length;
    const other = results.length - applied - changed;
    let summary: string;
    if (stopped) {
      summary = applied
        ? `Applied ${applied} of ${pending.length}, then stopped: Word won't change this document (protected or read-only).`
        : "Nothing applied: Word won't change this document (protected or read-only).";
    } else {
      const parts = [`Applied ${applied} of ${results.length}`];
      if (unverified) parts.push(`${unverified} to check (spacing Word added)`);
      if (changed) parts.push(`${changed} where the document changed`);
      if (other) parts.push(`${other} couldn't be placed`);
      summary = `${parts.join(" · ")}.`;
    }
    this.patch({ applyAll: { ...this.state.applyAll, [turnId]: { running: false, summary } } });
    await this.refreshTrackingMode();
    return results;
  }

  /** Add the Word comment of an applied edit whose comment failed. Never re-applies the edit (red-team S3). */
  async retryComment(turnId: string, editId: string): Promise<boolean> {
    const hands = this.deps.hands;
    const card = this.card(turnId, editId);
    const retry = card.retryComment;
    if (hands === null || !retry || card.commentBusy) return false;
    this.deps.onUserAction();
    this.setCard(turnId, editId, { commentBusy: true });
    let result: Awaited<ReturnType<DocumentHands["addComment"]>>;
    try {
      result = await hands.addComment(retry);
    } catch (error) {
      result = { status: "error", detail: error instanceof Error ? error.message : String(error) };
    }
    // Keep the edit's own warning (e.g. unverified spacing) whatever the comment does.
    const warning = card.status.state === "done" && card.status.warning ? { warning: card.status.warning } : {};
    if (result.status === "added") {
      this.rememberComment(result.commentId, turnId, editId, false);
      const where = result.commentOnNote ? noteCommentText(result.commentOnNote) : "";
      this.setCard(turnId, editId, {
        commentBusy: false,
        retryComment: null,
        status: { state: "done", text: `Applied as a tracked change. Comment added.${where}`, ...warning },
      });
      return true;
    }
    if (result.status === "protected") this.protectedDocument();
    this.setCard(turnId, editId, {
      commentBusy: false,
      status: { state: "done", text: `Applied as a tracked change. The comment still couldn't be added: ${result.detail}`, ...warning },
    });
    return false;
  }

  async addComment(turnId: string, commentId: string, text?: string): Promise<CardStatus | null> {
    const hands = this.deps.hands;
    const turn = this.turn(turnId);
    const comment = turn?.artifacts?.comments.find((c) => c.comment_id === commentId);
    if (turn === undefined || comment === undefined) return null;
    const body = (text ?? comment.text).trim();
    if (!body) return null;
    this.deps.onUserAction();
    this.setCard(turnId, commentId, { status: { state: "working" } });
    let status: CardStatus;
    if (hands === null) {
      status = { state: "failed", reason: "error", text: "Open this pane inside Word to add comments." };
    } else {
      const fresh = await this.freshness(turn);
      const hint = this.anchorHint(turn, comment.pid, comment.paragraph_hash);
      const result = await hands.addComment({ ...hint, quote: comment.quote, text: body });
      if (result.status === "protected") {
        this.setCard(turnId, commentId, { status: { state: "idle" } });
        this.protectedDocument();
        return { state: "idle" };
      }
      if (result.status === "added") {
        this.rememberComment(result.commentId, turnId, commentId, true);
        status = { state: "done", text: `Comment added.${result.commentOnNote ? noteCommentText(result.commentOnNote) : ""}` };
      } else if ((result.status === "not_found" || result.status === "ambiguous") && this.changedHere(turn, comment.pid, fresh)) {
        status = { state: "failed", reason: "changed", text: `Document changed here. ${result.detail}` };
      } else {
        status = { state: "failed", reason: result.status === "error" ? "error" : "place", text: `Couldn't place: ${result.detail}` };
      }
    }
    this.setCard(turnId, commentId, { status });
    this.reportStatus(turnId, commentId, comment.pid, status, "applied");
    return status;
  }

  async insert(turnId: string, insertId: string, at: InsertRequest["at"]): Promise<CardStatus | null> {
    const hands = this.deps.hands;
    const turn = this.turn(turnId);
    const item = turn?.artifacts?.inserts.find((i) => i.insert_id === insertId);
    if (turn === undefined || item === undefined) return null;
    this.deps.onUserAction();
    this.setCard(turnId, insertId, { status: { state: "working" } });
    let status: CardStatus;
    if (hands === null) {
      status = { state: "failed", reason: "error", text: "Open this pane inside Word to insert text." };
    } else {
      const request: InsertRequest = { at, markdown: item.markdown, mode: "tracked", journal: await this.journal() };
      if (at === "after_pid") {
        const hint = this.anchorHint(turn, item.pid, item.paragraph_hash);
        request.pid = hint.pid;
        if (hint.paragraphHash) request.paragraphHash = hint.paragraphHash;
      } else {
        // The selection/cursor as Ella saw it — never whatever a ¶ link or Show button selected since (M5).
        request.target = turn.selectionTarget;
      }
      const result = await hands.insertText(request);
      const restore = result.modeRestoreFailed ? ` ${RESTORE_WARNING}` : "";
      if (result.modeRestoreFailed) {
        this.patch({ notice: { tone: "error", text: RESTORE_WARNING } });
        await this.checkLeftOffTracking();
      }
      if (result.status === "protected") {
        this.setCard(turnId, insertId, { status: { state: "idle" } });
        this.protectedDocument();
        return { state: "idle" };
      }
      if (result.status === "inserted") {
        const where = at === "after_pid" ? `after ${pidLabel(item.pid)}` : at === "cursor" ? "at the cursor" : "in place of the selection";
        status = { state: "done", text: `Inserted ${where} as a tracked change.${restore}` };
      } else if (result.status === "refused") {
        status = { state: "failed", reason: "refused", text: result.detail + restore };
      } else {
        status = { state: "failed", reason: result.status === "error" ? "error" : "place", text: `Couldn't insert: ${result.detail}${restore}` };
      }
    }
    this.setCard(turnId, insertId, { status });
    this.reportStatus(turnId, insertId, item.pid, status, "applied");
    await this.refreshTrackingMode();
    return status;
  }

  // ---- Track Changes left off (spec §5.2) ------------------------------------------------------------------

  /**
   * On pane load (and after a failed restore): if a note says Ella switched Track Changes on for this document
   * and the mode is still not what it was, offer to put it back. A note whose mode is already right is dropped.
   */
  async checkLeftOffTracking(): Promise<void> {
    const hands = this.deps.hands;
    if (hands === null) return;
    const store = this.store();
    const record = readFlip(store);
    if (record === null) return;
    const now = (this.deps.now ?? Date.now)();
    if (now - record.startedAt > FLIP_MAX_AGE_MS) {
      clearFlip(store);
      return;
    }
    // Never mint a doc_key just to look (minting writes into the document).
    const current = this.docKeyPromise !== null ? (await this.docKeyPromise).key : peekDocKey(this.deps.docSettings);
    if (current !== null ? current !== record.docKey : record.persisted) return; // another document's note
    let mode: TrackingMode;
    try {
      mode = await hands.getTrackingMode();
    } catch {
      return;
    }
    if (mode === record.previousMode) {
      clearFlip(store);
      if (this.state.trackingLeftOff !== null) this.patch({ trackingLeftOff: null });
      return;
    }
    this.patch({ trackingLeftOff: { previousMode: record.previousMode, currentMode: mode } });
  }

  /** The banner's one click: put Track Changes back to what it was before Ella switched it. */
  async restoreLeftOffTracking(): Promise<boolean> {
    const hands = this.deps.hands;
    const left = this.state.trackingLeftOff;
    if (hands === null || left === null) return false;
    this.deps.onUserAction();
    try {
      await hands.setTrackingMode(left.previousMode);
    } catch (error) {
      this.patch({ notice: { tone: "error", text: `Couldn't change Track Changes: ${error instanceof Error ? error.message : String(error)}` } });
      return false;
    }
    clearFlip(this.store());
    this.patch({ trackingLeftOff: null, notice: { tone: "info", text: `Track Changes is ${modeLabel(left.previousMode)} again, as you had it.` } });
    await this.refreshTrackingMode();
    return true;
  }

  dismissLeftOffTracking(): void {
    clearFlip(this.store());
    this.patch({ trackingLeftOff: null });
  }

  async jump(pid: string, quote?: string): Promise<void> {
    const hands = this.deps.hands;
    if (hands === null) return;
    let result = await hands.navigate(pid, quote || undefined);
    // The quote may be the reviewed text (pending changes accepted) or have moved: show the paragraph instead.
    if (result.status !== "selected" && quote) result = await hands.navigate(pid);
    if (result.status !== "selected") this.patch({ notice: { tone: "info", text: `Couldn't find that spot in the document. ${result.detail}` } });
  }

  /** Delete the Word comments this pane created this session — never anyone else's. */
  async removeEllaComments(): Promise<{ removed: string[]; missing: string[] } | null> {
    const hands = this.deps.hands;
    const ids = [...this.state.ellaCommentIds];
    if (hands === null || ids.length === 0) return null;
    this.deps.onUserAction();
    let outcome: { removed: string[]; missing: string[] };
    try {
      outcome = await hands.removeComments(ids);
    } catch (error) {
      this.patch({ notice: { tone: "error", text: `Couldn't remove the comments: ${error instanceof Error ? error.message : String(error)}` } });
      return null;
    }
    const gone = new Set([...outcome.removed, ...outcome.missing]);
    const cards = { ...this.state.cards };
    for (const id of gone) {
      const origin = this.commentCards.get(id);
      this.commentCards.delete(id);
      // A comment card whose comment is gone can be added again; an edit card keeps its tracked change.
      const current = origin ? cards[origin.key] : undefined;
      if (origin?.commentCard && current) cards[origin.key] = { ...current, status: { state: "idle" } };
    }
    const n = outcome.removed.length;
    const text =
      `Removed ${n} of Ella's comment${n === 1 ? "" : "s"}.` +
      (outcome.missing.length ? ` ${outcome.missing.length} had already been deleted.` : "");
    this.patch({ cards, ellaCommentIds: ids.filter((id) => !gone.has(id)), notice: { tone: "info", text } });
    void this.syncEllaComments([], [...gone]);
    return outcome;
  }

  // ---- saved versions (spec §3.3) ----------------------------------------------------------------------------

  /** Versions of this document from every conversation; restores the ids of Ella's comments they hold. */
  async loadVersions(): Promise<void> {
    const token = this.deps.token();
    if (token === null) return;
    const generation = this.generation;
    let versions: SavedVersion[];
    try {
      const key = await this.docKey();
      versions = await this.deps.backend.listVersions(token, key.key);
    } catch (error) {
      if (generation !== this.generation) return;
      if (error instanceof AuthLostError) this.deps.onAuthLost();
      this.patch({ versionsLoaded: true });
      return;
    }
    if (generation !== this.generation) return;
    const known = new Set(this.state.ellaCommentIds);
    const restored: string[] = [];
    for (const id of versions.flatMap((v) => v.ellaCommentIds)) {
      if (known.has(id)) continue;
      known.add(id);
      restored.push(id);
    }
    this.patch({
      versions: sortVersions(versions),
      versionsLoaded: true,
      ellaCommentIds: restored.length ? [...this.state.ellaCommentIds, ...restored] : this.state.ellaCommentIds,
    });
  }

  /** Versions shown for this conversation (all of them before the first turn names one). */
  visibleVersions(): SavedVersion[] {
    const thread = this.state.threadId;
    return this.state.versions.filter((v) => thread === null || v.threadId === thread);
  }

  /** Save the document as it is now under `name`, in this conversation. */
  async saveVersion(rawName: string): Promise<SavedVersion | null> {
    const name = rawName.replace(/\s+/g, " ").trim().slice(0, 80);
    const hands = this.deps.hands;
    const token = this.deps.token();
    const threadId = this.state.threadId;
    if (!name || hands === null || this.state.versionBusy || this.state.busy) return null;
    if (token === null) {
      this.deps.onAuthLost();
      return null;
    }
    if (threadId === null) {
      this.patch({ notice: { tone: "info", text: "Send Ella a message first: a version belongs to a conversation." } });
      return null;
    }
    this.deps.onUserAction();
    const generation = this.generation;
    this.patch({ versionBusy: true, notice: null });
    try {
      const key = await this.docKey();
      const captured = await hands.capture();
      const ticket = await this.deps.backend.initSnapshot(token);
      const snapshot: WordSnapshot = { ...captured.snapshot, snapshot_id: ticket.snapshotId };
      await this.deps.backend.uploadSnapshot(ticket, snapshot);
      let version = await this.deps.backend.saveVersion(token, { snapshotId: ticket.snapshotId, docKey: key.key, threadId, name });
      if (generation !== this.generation) return null;
      // The newest version keeps the ids of the comments Ella added so far.
      const ids = this.state.ellaCommentIds;
      for (let i = 0; i < ids.length; i += ELLA_ID_CHUNK) {
        version = await this.deps.backend.updateEllaComments(token, version.versionId, ids.slice(i, i + ELLA_ID_CHUNK), []);
      }
      this.patch({ versions: sortVersions([version, ...this.state.versions]), notice: { tone: "info", text: `Saved version “${version.name}”.` } });
      return version;
    } catch (error) {
      if (generation !== this.generation) return null;
      if (error instanceof AuthLostError) this.deps.onAuthLost();
      this.patch({ notice: { tone: "error", text: `Couldn't save the version. ${error instanceof Error ? error.message : String(error)}` } });
      return null;
    } finally {
      if (generation === this.generation) this.patch({ versionBusy: false });
    }
  }

  async deleteVersion(versionId: string): Promise<boolean> {
    const token = this.deps.token();
    if (token === null || this.state.versionBusy) return false;
    this.deps.onUserAction();
    this.patch({ versionBusy: true });
    try {
      await this.deps.backend.deleteVersion(token, versionId);
      this.patch({ versions: this.state.versions.filter((v) => v.versionId !== versionId) });
      return true;
    } catch (error) {
      if (error instanceof AuthLostError) this.deps.onAuthLost();
      this.patch({ notice: { tone: "error", text: `Couldn't delete the version. ${error instanceof Error ? error.message : String(error)}` } });
      return false;
    } finally {
      this.patch({ versionBusy: false });
    }
  }

  /**
   * The prompt that asks Ella to compare with `versionId`. A version saved in another conversation belongs to
   * that conversation (Ella can only compare within it), so choosing one continues that conversation here.
   */
  async compareWith(versionId: string): Promise<string | null> {
    const version = this.state.versions.find((v) => v.versionId === versionId);
    if (version === undefined || this.state.busy) return null;
    if (version.threadId !== this.state.threadId) {
      const key = await this.docKey();
      if (this.state.turns.length > 0) this.patch({ turns: [], cards: {}, applyAll: {} });
      this.reports.clear();
      this.reported.clear();
      this.setThread(key.key, version.threadId);
      this.patch({ notice: { tone: "info", text: `Continuing the conversation where “${version.name}” was saved.` } });
    }
    return comparePrompt(version);
  }

  /** Keep the newest version's list of Ella's comment ids in step (best effort; memory stays the source). */
  private async syncEllaComments(add: readonly string[], remove: readonly string[]): Promise<void> {
    const token = this.deps.token();
    if (token === null) return;
    const targets = new Map<string, { add: string[]; remove: string[] }>();
    const newest = this.state.versions[0];
    if (add.length && newest !== undefined) targets.set(newest.versionId, { add: [...add], remove: [] });
    if (remove.length) {
      const gone = new Set(remove);
      for (const v of this.state.versions) {
        const held = v.ellaCommentIds.filter((id) => gone.has(id));
        if (!held.length) continue;
        const entry = targets.get(v.versionId) ?? { add: [], remove: [] };
        entry.remove.push(...held);
        targets.set(v.versionId, entry);
      }
    }
    for (const [versionId, change] of targets) {
      try {
        let updated: SavedVersion | null = null;
        for (let i = 0; i < Math.max(change.add.length, 1); i += ELLA_ID_CHUNK) {
          updated = await this.deps.backend.updateEllaComments(token, versionId, change.add.slice(i, i + ELLA_ID_CHUNK), i === 0 ? change.remove : []);
        }
        if (updated !== null) {
          const saved = updated;
          this.patch({ versions: this.state.versions.map((v) => (v.versionId === versionId ? saved : v)) });
        }
      } catch {
        // The ids stay in pane memory; the next change or save tries again.
      }
    }
  }
}

function sortVersions(versions: readonly SavedVersion[]): SavedVersion[] {
  const unique = new Map(versions.map((v) => [v.versionId, v]));
  return [...unique.values()].sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0));
}
